import { useCallback, useRef } from 'react';
import type { AgentTeamRoomResult, CreateAgentTeamRoomParams } from '$/hooks/useCreateRoom';
import useCreateRoom from '$/hooks/useCreateRoom';
import { getVideoCoverUrl } from '$/utils/upload';
import { useCommonStore } from '@/store/useCommonStore';
import {
  addExternalMedias,
  type ExternalCacheDataItem,
} from '$/pages/RoboneoTeamFlow/utils/addAssetNodes';

export interface AiToolWorkflowMedia {
  url: string;
  type: 'image' | 'video' | 'audio' | 'markdown' | 'text' | 'script';
  name?: string;
  assetId?: number;
  fileExtension?: string;
  materialType?: string;
  width?: number;
  height?: number;
  duration?: number;
  coverUrl?: string;
  raw?: Record<string, any>;
}

export interface OpenAiToolResultInTeamStudioOptions {
  trackLocation?: string;
}

type CreateRoomFn = (params?: CreateAgentTeamRoomParams) => Promise<AgentTeamRoomResult | undefined>;

/** 从 URL 中提取文件名，作为工作流节点名称的兜底值。 */
function getMediaName(media: AiToolWorkflowMedia, index: number) {
  if (media.name) return media.name;
  try {
    const { pathname } = new URL(media.url);
    const fileName = decodeURIComponent(pathname.split('/').pop() || '');
    if (fileName) return fileName;
  } catch (error) {
    console.warn('[aiToolsOpenInTeamStudio] parse media name failed', error);
  }
  return `${media.type}-${index + 1}`;
}

/** 只把普通对象作为 raw 继承，避免数组或空值污染节点元数据。 */
function getRawRecord(value: unknown): Record<string, any> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? value as Record<string, any>
    : {};
}

/** 将 AI 工具产物转换为新版 team_studio 可消费的外部媒体缓存结构。 */
function buildExternalMediaItem(media: AiToolWorkflowMedia, index: number): ExternalCacheDataItem {
  const name = getMediaName(media, index);
  const baseRaw = getRawRecord(media.raw);
  const width = media.width ?? (typeof baseRaw.width === 'number' ? baseRaw.width : 0);
  const height = media.height ?? (typeof baseRaw.height === 'number' ? baseRaw.height : 0);
  const duration = media.duration ?? (typeof baseRaw.duration === 'number' ? baseRaw.duration : 0);
  const baseFields = {
    ...(media.assetId != null ? { assetId: media.assetId } : {}),
    ...(media.fileExtension ? { fileExtension: media.fileExtension } : {}),
  };

  if (media.type === 'markdown' || media.type === 'text' || media.type === 'script') {
    return {
      ...baseFields,
      fileExtension: media.fileExtension || 'md',
      materialType: media.type === 'markdown' ? (media.materialType || 'document') : media.type,
      mediaUrl: media.url,
      raw: { ...baseRaw, name },
    };
  }

  if (media.type === 'audio') {
    const rawAudio = getRawRecord(baseRaw.audio);
    return {
      ...baseFields,
      materialType: 'audio',
      mediaUrl: media.url,
      raw: {
        ...baseRaw,
        name,
        duration,
        audio: {
          ...rawAudio,
          duration: media.duration ?? rawAudio.duration ?? duration,
        },
      },
    };
  }

  if (media.type === 'video') {
    const coverUrl = media.coverUrl || getVideoCoverUrl(media.url);
    const rawVideo = getRawRecord(baseRaw.video);
    return {
      ...baseFields,
      materialType: 'video',
      mediaUrl: media.url,
      coverUrl,
      raw: {
        ...baseRaw,
        name,
        width,
        height,
        duration,
        video: {
          ...rawVideo,
          width: media.width ?? rawVideo.width ?? width,
          height: media.height ?? rawVideo.height ?? height,
          duration: media.duration ?? rawVideo.duration ?? duration,
        },
      },
    };
  }

  const rawImage = getRawRecord(baseRaw.image);
  return {
    ...baseFields,
    materialType: 'image',
    mediaUrl: media.url,
    raw: {
      ...baseRaw,
      name,
      width,
      height,
      image: {
        ...rawImage,
        width: media.width ?? rawImage.width ?? width,
        height: media.height ?? rawImage.height ?? height,
      },
    },
  };
}

/** 打开新版 team_studio，并在进入房间后把 AI 工具结果图渲染为工作流节点。 */
export default function useOpenAiToolResultInTeamStudio() {
  const initUserInfo = useCommonStore((state) => state.initUserInfo);
  const pendingMediaListRef = useRef<AiToolWorkflowMedia[] | null>(null);
  const pendingOptionsRef = useRef<OpenAiToolResultInTeamStudioOptions | null>(null);
  const createRoomRef = useRef<CreateRoomFn | null>(null);
  const { loading, createRoom } = useCreateRoom({
    onLoginSuccessed: async () => {
      await initUserInfo();
      const pendingMediaList = pendingMediaListRef.current;
      if (!pendingMediaList?.length) return;
      addExternalMedias(pendingMediaList.map(buildExternalMediaItem));
      pendingMediaListRef.current = null;
      const trackLocation = pendingOptionsRef.current?.trackLocation ?? 'ai_tools';
      pendingOptionsRef.current = null;
      await createRoomRef.current?.({ trackLocation });
    },
  });

  createRoomRef.current = createRoom;

  /** 点击“在工具流中打开”时先缓存外部媒体，再创建并跳转新版 team_studio 房间。 */
  const openInTeamStudio = useCallback(async (
    mediaList: AiToolWorkflowMedia[],
    options?: OpenAiToolResultInTeamStudioOptions,
  ) => {
    if (!mediaList.length) return false;
    pendingMediaListRef.current = mediaList;
    pendingOptionsRef.current = options ?? null;
    addExternalMedias(mediaList.map(buildExternalMediaItem));
    const result = await createRoom({ trackLocation: options?.trackLocation ?? 'ai_tools' });
    if (result) {
      pendingMediaListRef.current = null;
      pendingOptionsRef.current = null;
    }
    return Boolean(result);
  }, [createRoom]);

  return {
    loading,
    openInTeamStudio,
  };
}
